import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  getMyVacancies,
  confirmAssignation,
  rejectAssignation,
  createVacancy,
  getSkills,
  getRequests,
  requestReject,
  editVacancy,
  deleteVacancy,
  getVacancyArchive,
  getOutsourceReport,
  createVacancyPack,
  confirmVacancyPack,
  getVacancyHistory
} from '@/api/exchange'

import { getMetabaseUrl } from '@/api/auth'

import { useToast } from 'vue-toast-notification'

const toast = useToast({ position: 'top' })

export const useExchangeStore = defineStore('exchange', () => {
  // Состояния
  const vacancies = ref<any[]>([])
  const history = ref<any[]>([])
  const vacanciesCount = ref<number>(0)
  const vacanciesParams = ref<any>(null)
  const vacancyArchive = ref<any[]>([])
  const vacancyArchiveCount = ref<number>(0)
  const requests = ref<any[]>([])
  const requestsCount = ref<number>(0)
  const requestsParams = ref<any>(null)
  const skills = ref<any[]>([])
  const loading = ref<boolean>(false)
  let vacancyArchiveRequest = 0

  // Статусы вакансий
  const vacancyStatuses = ref([
    {
      name: 'Закрыта',
      value: 'completed'
    },
    {
      name: 'Открыта',
      value: 'open'
    },
    {
      name: 'Ожидание',
      value: 'responded'
    }
  ])

  // Статусы запросов
  const requestStatuses = ref([
    {
      name: 'Закрыт',
      value: 'completed'
    },
    {
      name: 'Отклонен',
      value: 'rejected'
    },
    {
      name: 'Ожидание',
      value: 'requested'
    }
  ])

  // Функции для работы с вакансиями

  async function fetchMyVacancies(data: any) {
    vacanciesParams.value = data
    try {
      const res = await getMyVacancies(data)
      vacancies.value = res.data.body.results
      vacanciesCount.value = res.data.body.count
    } catch (err: any) {
      return err
    }
  }

  async function fetchVacancyArchive(data: any) {
    const request = ++vacancyArchiveRequest
    loading.value = true
    try {
      const res = await getVacancyArchive(data)
      if (request === vacancyArchiveRequest) {
        vacancyArchive.value = res.data.body.results
        vacancyArchiveCount.value = res.data.body.count
      }
    } catch (err: any) {
      return err
    } finally {
      if (request === vacancyArchiveRequest) loading.value = false
    }
  }

  async function fetchRequests(data: any) {
    requestsParams.value = data
    try {
      const res = await getRequests(data)
      requests.value = res.data.body.results
      requestsCount.value = res.data.body.count
    } catch (err: any) {
      return err
    }
  }

  async function rejectRequest(id: string) {
    try {
      loading.value = true
      await requestReject(id)
      await fetchRequests(requestsParams)
      loading.value = false
    } catch (err: any) {
      return err
    }
  }

  async function confirmVacancyAssignation(id: string, userId: string) {
    try {
      const res = await confirmAssignation(id, userId)
      if (res.data) {
        toast.success(res.data.msg)
      }
      await fetchMyVacancies(vacanciesParams)
    } catch (err: any) {
      return err
    }
  }

  async function rejectVacancyAssignation(id: string, userId: string) {
    try {
      const res = await rejectAssignation(id, userId)
      if (res.data) {
        toast.success(res.data.msg)
      }
      await fetchMyVacancies(vacanciesParams)
    } catch (err: any) {
      return err
    }
  }

  async function fetchSkills() {
    try {
      const res = await getSkills()
      skills.value = res.data.body
    } catch (err: any) {
      return err
    }
  }

  async function fetchHistory(id:string) {
    try {
      const res = await getVacancyHistory(id)
      if(res.data.status === 'success') {
        history.value = res.data.body
      }
    } catch (err: any) {
      return err
    }
  }

  async function fetchMetabaseUrl(data: any) {
    try {
      const res = await getMetabaseUrl(data)
      return res.data.body.url
    } catch (err: any) {
      return err
    }
  }

  async function addVacancy(data: any) {
    try {
      const res = await createVacancy(data)
      toast.success('Вакансия создана успешно')
      await fetchMyVacancies(vacanciesParams.value)
      await fetchRequests(requestsParams.value)
      return res
    } catch (err: any) {
      return err
    }
  }

  async function addVacancyPack(data: any) {
    try {
      const res = await createVacancyPack(data)
      await fetchMyVacancies(vacanciesParams.value)
      return res
    } catch (err: any) {
      return err
    }
  }

  async function confirmVacancyBulk() {
    try {
      const res = await confirmVacancyPack()
      toast.success(res.data?.msg)
      await fetchMyVacancies(vacanciesParams.value)
      return res
    } catch (err: any) {
      return err
    }
  }

  async function changeVacancy(data: any) {
    try {
      const res = await editVacancy(data)
      toast.success('Вакансия сохранена успешно')
      await fetchMyVacancies(vacanciesParams.value)
      return res
    } catch (err: any) {
      return err
    }
  }

  async function removeVacancy(id: string, purpose: string) {
    const data = {
      deletion_reason: purpose
    }
    try {
      const res = await deleteVacancy(id, data)
      toast.success('Вакансия удалена успешно')
      await fetchMyVacancies(vacanciesParams.value)
      return res
    } catch (err: any) {
      return err
    }
  }

  async function fetchOutsourceReport(data: any) {
    const reportName = "Отчет по работе АК"
    try {
      const res = await getOutsourceReport(data)
      if (res.data) {
        toast.success('Скачивание начнется автоматически')
        const blob = new Blob([res.data]); // Get the response as a Blob
        const url = window.URL.createObjectURL(blob); // Create a URL for the Blob

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportName} за ${data.date_from} - ${data.date_to}.xlsx`); // Set the desired filename and extension
        document.body.appendChild(link);
        link.click(); // Programmatically click the link to trigger download
        document.body.removeChild(link); // Clean up the link element
        window.URL.revokeObjectURL(url); // Release the object URL
      }

    } catch (err: any) {
      return err
    }
  }

  return {
    vacancies,
    history,
    vacanciesCount,
    loading,
    skills,
    vacancyStatuses,
    requests,
    requestsCount,
    requestStatuses,
    vacancyArchive,
    vacancyArchiveCount,
    fetchMyVacancies,
    confirmVacancyAssignation,
    rejectVacancyAssignation,
    addVacancy,
    fetchSkills,
    fetchMetabaseUrl,
    fetchRequests,
    rejectRequest,
    changeVacancy,
    removeVacancy,
    fetchVacancyArchive,
    fetchOutsourceReport,
    addVacancyPack,
    confirmVacancyBulk,
    fetchHistory
  }
})
